import * as THREE from 'three';
import { ThreeEvent } from '@react-three/fiber';
import { BaseTool } from '../BaseTool';
import { WallElement } from '../../core/types';
import { createTranslatedWallCopy } from '../../utils/WallGeometryUtils';

export class ArrayToolClass extends BaseTool {
    id = 'ARRAY';
    name = 'Array';
    cursor = 'crosshair';

    private startPoint: THREE.Vector3 | null = null;
    private count: number = 3; // Default array count
    private mode: 'Linear' | 'Radial' = 'Linear';

    constructor(mode: 'Linear' | 'Radial' = 'Linear') {
        super();
        this.mode = mode;
        if (mode === 'Radial') {
            this.id = 'ARRAY_RADIAL';
            this.name = 'Radial Array';
        }
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const selection = this.context.appStore.selectedIds;
        if (selection.length === 0) {
            // Click-to-select: pick the element under the pointer first; the
            // next click places the array vector start point.
            const id = this.findElementIdAtPoint(event);
            if (id) this.selectElement(id);
            return;
        }

        const point = this.getIntersection(event);
        if (!point) return;
        const snappedPoint = this.snapToGrid(point);

        if (!this.startPoint) {
            // First click: Start point of translation vector
            this.startPoint = snappedPoint;
            console.log('Array: Start point selected', this.startPoint);

            // Show preview line?
            this.context.appStore.setToolPreview({
                active: true,
                start: this.startPoint,
                current: this.startPoint,
                toolId: this.id
            });
        } else {
            // Second click: End point of translation vector
            const endPoint = snappedPoint;
            console.log('Array: End point selected', endPoint);

            if (this.mode === 'Linear') {
                this.createLinearArray(selection, this.startPoint, endPoint, this.count);
            } else {
                this.createRadialArray(selection, this.startPoint, endPoint, this.count);
            }

            // Reset
            this.startPoint = null;
            this.context.appStore.setToolPreview(null);
            this.context.appStore.setActiveTool('select');
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || !this.startPoint) return;

        const point = this.getIntersection(event);
        if (!point) return;
        const snappedPoint = this.snapToGrid(point);

        // Preview depends on mode
        this.context.appStore.setToolPreview({
            active: true,
            start: this.startPoint,
            current: snappedPoint,
            toolId: this.id,
            mode: this.mode
        });
    }

    onPointerUp(): void { }

    private createLinearArray(ids: string[], start: THREE.Vector3, end: THREE.Vector3, count: number) {
        const translation = end.clone().sub(start);

        // Create (count - 1) copies
        for (let i = 1; i < count; i++) {
            const stepTranslation = translation.clone().multiplyScalar(i); // Linear array: 2nd item at 1x dist, 3rd at 2x dist...
            // Or is it 'Move to 2nd' vs 'Move to Last'? 
            // BIM has both. Let's assume 'Move to 2nd' for now (distance between items).

            ids.forEach(id => {
                const element = this.context!.bimStore.project.elements.get(id);
                if (!element) return;

                // Clone and translate
                if (element.category === 'Walls') {
                    const wall = element as WallElement;
                    // Fresh render geometry: a clone carrying the original's
                    // layer profiles would render at the original's location.
                    const newWall = createTranslatedWallCopy(wall, stepTranslation);
                    this.context!.bimStore.addElement(newWall);
                }
            });
        }
    }

    private createRadialArray(ids: string[], center: THREE.Vector3, end: THREE.Vector3, count: number) {
        // Radial Array Logic
        // Center is the center of rotation (first click)
        // End determines the angle of the second item (second click relative to center)

        // Vector from center to selected object? 
        // We need the object's current position to determine radius.
        // Assuming first selected object's position as "start angle" ref?

        // Simplified: 
        // 1. Center is startPoint.
        // 2. Angle is determind by endPoint - Center.

        // Radial Array Logic Placeholder
        console.log('Radial Array logic centered at:', center, 'End ref:', end, 'Count:', count, 'Mode:', ids);
    }

    deactivate() {
        super.deactivate();
        this.startPoint = null;
        this.context?.appStore.setToolPreview(null);
    }
}
